import { useEffect, useRef, useState } from "react";
import { Smiley } from "@phosphor-icons/react";

const EMOJIS = [
  "📈", "📉", "🚀", "🔥", "💎", "🐂", "🐻", "🎯",
  "🤖", "🧠", "⚡", "🏆", "💰", "💸", "🎲", "🌊",
  "☕", "🌙", "☀️", "🎨", "🏄", "🎧", "🌱", "🍀",
  "🧘", "🎢", "🛡️", "⚙️", "🔮", "🎓", "🧪", "📊",
];

/**
 * Compact emoji picker for short text inputs (like the 60-char bio).
 * Inserts an emoji at the current cursor position when possible.
 */
export default function EmojiPicker({ value, onChange, inputRef, disabled = false, testId }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close popover on outside-click / Escape so it feels like a native picker
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const insert = (emoji) => {
    const el = inputRef?.current;
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = value.slice(0, start) + emoji + value.slice(end);
      onChange(next);
      // Restore caret just after the inserted emoji on the next tick
      requestAnimationFrame(() => {
        const pos = start + emoji.length;
        el.focus();
        try { el.setSelectionRange(pos, pos); } catch {}
      });
    } else {
      onChange((value || "") + emoji);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        data-testid={testId || "emoji-picker-toggle"}
        className="text-zinc-400 hover:text-white p-1.5 border border-zinc-800 rounded-sm"
        title="Insert emoji"
      >
        <Smiley size={12} />
      </button>
      {open && (
        <div
          data-testid="emoji-picker-popover"
          className="absolute z-20 top-full mt-2 right-0 bg-zinc-900 border border-zinc-700 rounded-sm shadow-2xl p-2 grid grid-cols-8 gap-1 w-64"
        >
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => insert(e)}
              className="hover:bg-zinc-800 rounded-sm p-1 text-lg leading-none"
              data-testid={`emoji-${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
